import { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { useAuthStore } from "../store";
import { optionsAdminAuth } from "./adminAuthMode";
import { redirectToOptionsLogin } from "./adminAuth";
import {
  getEquionSession,
  isEquionSessionInvalid,
  redirectToAdminSignIn,
  refetchAccessToken,
  tokenMatchesEquionSession,
} from "./idServer";

interface RetriableRequest extends AxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string> | null = null;

export function attachAuthRefresh(api: AxiosInstance, headerName: string) {
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const request = error.config as RetriableRequest | undefined;
      const requestToken = request?.headers?.[headerName] as string | undefined;
      if (
        error.response?.status === 401 &&
        JSON.stringify(error.response.data || {}).includes(
          "Session has been revoked"
        )
      ) {
        window.dispatchEvent(new Event("operational-control-session-revoked"));
        return Promise.reject(error);
      }
      if (optionsAdminAuth) {
        if (
          headerName === "Authorization" &&
          error.response?.status === 401 &&
          requestToken &&
          !request?.url?.endsWith("/login") &&
          !request?.url?.endsWith("/logout") &&
          !request?.url?.endsWith("/session")
        ) {
          redirectToOptionsLogin();
        }
        return Promise.reject(error);
      }
      if (
        error.response?.status !== 401 ||
        !request ||
        request._retry ||
        !requestToken ||
        request.url?.endsWith("/login") ||
        request.url?.endsWith("/logout") ||
        request.url === "exit"
      ) {
        return Promise.reject(error);
      }

      request._retry = true;
      const session = getEquionSession();
      if (!session || !tokenMatchesEquionSession(requestToken, session)) {
        redirectToAdminSignIn();
        return Promise.reject(error);
      }

      const currentToken = useAuthStore.getState().accessToken;
      if (
        currentToken &&
        currentToken !== requestToken &&
        tokenMatchesEquionSession(currentToken, session)
      ) {
        request.headers![headerName] = currentToken;
        return api(request);
      }

      const pending = refreshPromise || refetchAccessToken(session);
      refreshPromise = pending;
      try {
        const token = await pending;
        const activeSession = getEquionSession();
        if (
          !activeSession ||
          activeSession.sid !== session.sid ||
          activeSession.userId !== session.userId ||
          !tokenMatchesEquionSession(token, session)
        ) {
          redirectToAdminSignIn();
          return Promise.reject(error);
        }
        sessionStorage.setItem("accesstoken", token);
        useAuthStore.setState((state) => ({
          ...state,
          accessToken: token,
          isAuthenticated: true,
        }));
        request.headers![headerName] = token;
        return api(request);
      } catch (refreshError) {
        if (isEquionSessionInvalid(refreshError)) {
          redirectToAdminSignIn();
        }
        return Promise.reject(refreshError);
      } finally {
        if (refreshPromise === pending) refreshPromise = null;
      }
    }
  );
}
